import React, { useMemo, useRef } from 'react';
import { Instant, ZonedDateTime } from '@js-joda/core';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Loading from 'components/Loading';
import { HOUR_IN_MILLISECONDS } from 'config';
import { MakePageProps } from 'modules/makePage';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import {
  UpdateDisclaimerOneDayCommand,
  UpdateDisclaimerSevenDaysCommand,
  useAcceptDisclaimerOneDayMutation,
  useAcceptDisclaimerSevenDaysMutation,
  useDisclaimerQuery,
  useProjectQuery
} from 'providers/api';
import { useAuthorisation } from 'providers/authorisation';
import { UseQueryResult } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG, DEFAULT_TIMEZONE, displayTemporal, licenceExpiryZoned
} from 'utils';
import useRoles from 'hooks/useRoles';
import { isPcUser } from 'utils/roles';
import InformationBox from './InformationBox';

/* eslint-disable react/jsx-one-expression-per-line */
export interface withLicenceAndDisclaimerConfig<
  TParams,
  TQueries extends UseQueryResult[] = []
> {
  Component: React.ComponentType<MakePageProps<TQueries>>;
  projectIdIkey?: string;
  pageDataHook?: (params: TParams) => TQueries;
}

const withLicenceAndDisclaimer = <
  TParams,
  TQueries extends UseQueryResult[] = []
>({
  Component,
  projectIdIkey = 'projectId',
  pageDataHook,
}: withLicenceAndDisclaimerConfig<TParams, TQueries>) => {
  const Wrapped: React.FC = () => {
    const {
      functions: { isAdmin },
      state: { user, loading: authoriseLoading },
    } = useAuthorisation();

    const isAdminUser = isAdmin();
    const params = useParams<Record<string, string | undefined>>();

    const projectId = params?.[projectIdIkey];
    if (!projectId) {
      throw new Error(i18n._('The Page component has been wrapped using withLicenceAndDisclaimer but the project id is not present in the URL.'));
    }

    const queries = (pageDataHook
      ? pageDataHook(params as unknown as TParams)
      : []) as unknown as TQueries;

    const acceptOneDay = useAcceptDisclaimerOneDayMutation(user.id);
    const acceptSevenDays = useAcceptDisclaimerSevenDaysMutation(user.id);

    const roles = useRoles();
    const isPatersonAndCooke = isPcUser(roles);

    const projectQuery = useProjectQuery(projectId, {
      staleTime: 24 * HOUR_IN_MILLISECONDS,
      enabled: !!projectId && !isAdminUser,
    });

    // The disclaimer decision is made once per page load. Never refetch in the background: a refetch
    // that flips the answer would unmount the wrapped page (and reload the mine model) under the user.
    const disclaimerQuery = useDisclaimerQuery(projectId, {
      enabled: !!projectId && !isAdminUser && !isPatersonAndCooke,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    // Latch acceptance. `isLicenced` is computed by the server against the server clock, so the browser
    // clock plays no part; and once the user has been let through they stay through for the life of this
    // page, exactly as admins and P&C users are. The disclaimer can only reappear on the next page load.
    const disclaimerAcceptedRef = useRef(false);
    if (disclaimerQuery.data?.isLicenced) {
      disclaimerAcceptedRef.current = true;
    }
    const disclaimerAccepted = disclaimerAcceptedRef.current;

    const handleAcceptDisclaimer = (days: 1 | 7) => {
      const baseCommand = {
        dateAccepted: Instant.now(),
        acceptedTimeZone: DEFAULT_TIMEZONE.id(),
        projectId,
      };
      const command: UpdateDisclaimerOneDayCommand | UpdateDisclaimerSevenDaysCommand = baseCommand;

      const mutate = days === 1 ? acceptOneDay.mutate : acceptSevenDays.mutate;

      // Re-read the (now accepted) status; the latch above then lets the page through without a full reload.
      mutate(command, { onSuccess: () => { disclaimerQuery.refetch(); } });
    };

    const useOfString = useMemo(() => {
      if (!projectQuery.data) return '';
      return `${projectQuery.data.client.name} / ${projectQuery.data.name}`;
    }, [projectQuery.data]);

    const isLoadingNonAdmin = !isAdminUser && (projectQuery.isLoading || disclaimerQuery.isLoading || authoriseLoading);

    if (isLoadingNonAdmin) {
      return <Loading />;
    }

    if (isAdminUser) {
      return <Component parentQueries={queries} />;
    }

    if (!projectQuery.data) {
      return <Box>{i18n._('Failed to load project')}</Box>;
    }

    const project = projectQuery.data;

    // Skip disclaimer check for P&C users
    if (isPatersonAndCooke) {
      return <Component parentQueries={queries} />;
    }

    if (!disclaimerQuery.data) {
      return <Box>{i18n._('Failed to load disclaimer information')}</Box>;
    }

    const licenceExpiryDate = licenceExpiryZoned(project);
    const licenceIsExpired = licenceExpiryDate
      ? ZonedDateTime.now().isAfter(licenceExpiryDate)
      : false;

    if (!disclaimerAccepted) {
      return (
        <InformationBox title={i18n._('Disclaimer')}>
          <p>
            <Trans id="This platform has been developed by MineSmart Solutions LTD for the exclusive use of">
              This platform has been developed by MineSmart Solutions LTD for the exclusive use of
            </Trans>{' '}
            {useOfString}.
          </p>
          <p>
            <Trans id="This Platform is intended as an analysis guideline only.
            Actual operating conditions must be verified and monitored during paste backfill operations.
            The use of this platform shall be at the sole risk of the user regardless of any fault or negligence of MineSmart Solutions LTD."
            >
              This Platform is intended as an analysis guideline only.
              Actual operating conditions must be verified and monitored during paste backfill operations.
              The use of this platform shall be at the sole risk of the user regardless of any fault or negligence of MineSmart Solutions LTD.
            </Trans>
          </p>
          {licenceExpiryDate && (
            <p>
              <Trans id="The license issued by MineSmart Solutions LTD will expire on:">
                The license issued by MineSmart Solutions LTD will expire on:
              </Trans>{' '}
              {displayTemporal(licenceExpiryDate, DEFAULT_SHORT_DATE_NO_TIMEZONE_CONFIG)}
            </p>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, width: '100%' }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleAcceptDisclaimer(1)}
            >
              <Trans id="Accept and Continue">Accept and Continue</Trans>
            </Button>
            <Box sx={{ ml: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleAcceptDisclaimer(7)}
              >
                <Trans id="Accept and mute for 7 days">Accept and mute for 7 days</Trans>
              </Button>
            </Box>
          </Box>
        </InformationBox>
      );
    }

    if (licenceIsExpired && !isPatersonAndCooke) {
      return (
        <InformationBox title={i18n._('Licence Expired')}>
          <p>
            <Trans id="There is no active licence for this project. Please contact MineSmart to acquire a new licence.">
              There is no active licence for this project. Please contact MineSmart to acquire a new licence.
            </Trans>
          </p>
        </InformationBox>
      );
    }

    // ---- All good ----
    return <Component parentQueries={queries} />;
  };

  return Wrapped;
};

export default withLicenceAndDisclaimer;
